import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  nameof,
  TimeAMPM,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { autorun } from "mobx";
import { BillingMethodType, NewPaymentRelatedSource } from "../enums";
import { MerchantAccount } from "./MerchantApplicationInputModel";
import { PaymentMethodModel } from "./PaymentMethodModels";
import { ContactReferenceModel } from "./ContactDetailPageModel";
import { PaymentDetailModel } from "./PaymentModels";
import { IAPIPaymentRefundModel, IAPIUpsertPaymentModel } from "../interfaces";
import { AccountSettingsStore } from "../store";

export class UpsertPaymentModel {
  id: string;
  description: string;
  amount: string;
  dueDate: string;
  newMethodIsPrimary: boolean;
  time: string;
  amPm: keyof typeof TimeAMPM;
  billingMethod: keyof typeof BillingMethodType;
  paymentMethod?: PaymentMethodModel;
  merchantAccount?: MerchantAccount;
  errors: InputErrorModel<UpsertPaymentModel>[];
  contact?: ContactReferenceModel;
  newPaymentGatewayMethodId: string;
  statementDescriptor: string;
  subscriptionId: string;
  source: keyof typeof NewPaymentRelatedSource;
  sourceId: string; //id of contact, subscription, invoice, checkout, purchaselinks
  isNewPaymentMethod: boolean;
  subscriptionName: string;
  invoiceNumber: string;
  previousRefundAmount: number;
  refundAmount: string;
  isRefund: boolean;
  refundDescription: string;
  previousRefundDate: string;
  isContactRequired: boolean;
  // "Email payment receipt" opt-in for a manually-entered (Money area) payment. Manual payments
  // do not email a receipt automatically; this flag drives whether the backend sends one.
  emailReceipt: boolean;
  constructor(data?: Partial<UpsertPaymentModel>) {
    this.id = data?.id || "";
    this.description = data?.description || "";
    this.newMethodIsPrimary = data?.newMethodIsPrimary || false;
    this.amount = data?.amount || "";
    this.dueDate = data?.dueDate || new Date().toISOString();
    this.time = data?.time || UtilityMethods.getHHMMFromDate(new Date());
    this.amPm = data?.amPm || UtilityMethods.getAMPMFromDate(new Date());
    this.billingMethod = data?.billingMethod || "ChargePayment";
    this.subscriptionName = data?.subscriptionName || "";
    this.refundAmount = data?.refundAmount || "";
    this.invoiceNumber = data?.invoiceNumber || "";
    this.refundDescription = data?.refundDescription || "";
    this.previousRefundAmount = data?.previousRefundAmount || 0;
    this.paymentMethod =
      data?.paymentMethod && new PaymentMethodModel(data.paymentMethod);
    this.merchantAccount =
      data?.merchantAccount && new MerchantAccount(data.merchantAccount);
    this.contact = data?.contact && new ContactReferenceModel(data.contact);
    this.previousRefundDate = data?.previousRefundDate || "";

    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<UpsertPaymentModel>(e);
        })) ||
      [];
    this.statementDescriptor = data?.statementDescriptor || "";
    this.subscriptionId = data?.subscriptionId || "";
    this.source = data?.source || NewPaymentRelatedSource.Manual;
    this.sourceId = data?.sourceId || "";
    this.emailReceipt = data?.emailReceipt || false;

    this.isNewPaymentMethod = data?.isNewPaymentMethod || false;
    this.newPaymentGatewayMethodId = data?.newPaymentGatewayMethodId || "";
    this.isRefund = data?.isRefund || false;
    this.isContactRequired = data?.isContactRequired !== false;
    if (!data) {
      this.time = UtilityMethods.getHHMMFromDate(new Date());
      this.amPm = UtilityMethods.getAMPMFromDate(new Date());
      this.dueDate = new Date().toISOString();
    }
    autorun(() => {
      this.validate(true);
    });
  }

  isShowPayButton(): boolean {
    const isValidDate = CoreInputModelUtil.validateDateField(
      "dueDate",
      this.dueDate,
      []
    );
    const isValidTime = CoreInputModelUtil.validateTime(
      this.time,
      "time",
      this.amPm,
      []
    );
    if (isValidDate && isValidTime) {
      // const isValid = CoreInputModelUtil.validateDateAndTime(
      //     nameof<UpsertPaymentModel>('dueDate'),
      //     nameof<UpsertPaymentModel>('time'),
      //     new Date(this.dueDate),
      //     this.time,
      //     this.amPm,
      //     [],
      //     0,
      // );

      if (isValidDate && isValidTime) {
        const dueDate = UtilityMethods.addTimeToDateWithoutOffset(
          new Date(this.dueDate),
          this.time,
          this.amPm
        );
        const currentDate = new Date();
        currentDate.setHours(
          currentDate.getHours(),
          currentDate.getMinutes(),
          0,
          0
        );

        if (dueDate <= currentDate) {
          return true;
        }
      }
    }

    return false;
  }

  hasError(field: keyof UpsertPaymentModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof UpsertPaymentModel): string {
    return this.errors.find((e) => e.field === field)?.message || '';
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<UpsertPaymentModel>[] = [];
    if (this.isRefund) {
      CoreInputModelUtil.validateDecimalValue(
        this.refundAmount,
        "refundAmount",
        newErrors,
        undefined,
        true
      );
      if (
        parseFloat(this.refundAmount) > parseFloat(this.amount) ||
        parseFloat(this.refundAmount) + this.previousRefundAmount >
        parseFloat(this.amount)
      ) {
        newErrors.push({
          field: "refundAmount",
          message: "Please enter valid amount",
        });
      }
    } else {
      CoreInputModelUtil.validateTextField(
        this.description,
        "description",
        newErrors
      );
      CoreInputModelUtil.validateDecimalValue(
        this.amount,
        "amount",
        newErrors,
        undefined,
        true
      );

      if (
        !(this.contact?.id || this.contact?.mindmeContactId) &&
        this.isContactRequired
      ) {
        CoreInputModelUtil.addError(
          "contact",
          newErrors,
          "Contact is required"
        );
      }

      if (!this.merchantAccount) {
        CoreInputModelUtil.addError(
          "merchantAccount",
          newErrors,
          "Merchant account is required"
        );
      }

      if (this.isNewPaymentMethod) {
        if (!this.merchantAccount?.isNMIMerchantAccount()) {
          CoreInputModelUtil.validateTextField(
            this.newPaymentGatewayMethodId,
            "newPaymentGatewayMethodId",
            newErrors
          );
        }
      } else {
        if (!this.paymentMethod) {
          CoreInputModelUtil.addError(
            "paymentMethod",
            newErrors,
            "Payment method is required"
          );
        }
      }

      if (new Date().toDateString() === new Date(this.dueDate).toDateString()) {
        const isValidDate = CoreInputModelUtil.validateDateField(
          "dueDate",
          this.dueDate,
          newErrors
        );
        if (!isValidDate) {
          CoreInputModelUtil.addError(
            "dueDate",
            newErrors,
            "Due date cannot be today"
          );
        }
      } else {
        const isValidDate = CoreInputModelUtil.validateDateField(
          "dueDate",
          this.dueDate,
          newErrors
        );
        const isValidTime = CoreInputModelUtil.validateTime(
          this.time,
          "time",
          this.amPm,
          newErrors
        );
        if (isValidDate && isValidTime) {
          CoreInputModelUtil.validateDateAndTime(
            nameof<UpsertPaymentModel>("dueDate"),
            nameof<UpsertPaymentModel>("time"),
            new Date(this.dueDate),
            this.time,
            this.amPm,
            newErrors,
            0
          );
        }
      }
    }
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  static createModelFromPaymentDetail(
    data: PaymentDetailModel
  ): UpsertPaymentModel {
    return new UpsertPaymentModel({
      id: data.id,
      description: data.description,
      amount: data.grossAmount.toString(), //AccountSettingsStore.formatAmount(data.grossAmount),
      dueDate: data.dueDate.toISOString(),
      paymentMethod: data.paymentMethod,
      merchantAccount: data.merchantAccount,
      billingMethod: data.billingMethod,
      contact: data.contact,
      subscriptionId: data.subscriptionId,
      time: UtilityMethods.getHHMMFromDate(data.dueDate),
      amPm: UtilityMethods.getAMPMFromDate(data.dueDate),
      subscriptionName: data.subscriptionName,
      invoiceNumber: data.invoiceNumber,
      isRefund: data.status === "Paid" ? true : false,
      previousRefundAmount: data.refundAmount,
      statementDescriptor: data.statementDescriptor,
      // Without this the edit form opened unticked and then saved false, wiping the choice the
      // user made when the payment was created.
      emailReceipt: data.emailReceipt,
    });
  }

  createRefundModel(): IAPIPaymentRefundModel {
    return {
      paymentTransactionId: this.id,
      accountId: AuthService.authData.accountId,
      amount: parseFloat(this.refundAmount),
      description: this.refundDescription,
    };
  }

  createPaymentAPIModel(
    contactId: string,
    mindmeContactId?: string
  ): IAPIUpsertPaymentModel {
    const today = new Date();
    const selectedDueDate = new Date(this.dueDate);
    const isToday = today.toDateString() === selectedDueDate.toDateString();
    let time = this.time;
    let amPm = this.amPm;
    if (isToday) {
      time = "12:00";
      amPm = TimeAMPM.AM;
    }

    return {
      id: this.id,
      accountId: AuthService.authData.accountId,
      contactId: contactId,
      mindmeContactId: mindmeContactId || "",
      description: this.description,
      amount: parseFloat(this.amount),
      dueDate: UtilityMethods.addTimeToDate(new Date(this.dueDate), time, amPm),
      paymentMethodId: this.paymentMethod?.id || "",
      merchantAccount: this.merchantAccount?.createAPIModel2() || ({} as any),
      billingMethod: this.billingMethod,
      isNewPaymentMethodPrimary: this.newMethodIsPrimary,
      statementDescriptor: this.statementDescriptor,
      subscriptionId: "",
      source: this.source,
      sourceId: this.sourceId,
      newPaymentGatewayMethodId: this.newPaymentGatewayMethodId,
      emailReceipt: this.emailReceipt,
    };
  }
}
